import {otsBaseApi} from "../../ots-api";
import {Site, SiteCountiesRequest, SitePatchDTO, SiteSearchCriteria} from "../../../model/site";
import {transportCategories} from "../../../model/transport-category";
import {containerLevelApi} from "../container-level/container-level-store";
import {indexesOf, removeUndefined} from "../../../util/util";
import {containerApi} from "../container/container-store";
import {objectChangeLogApi} from "../object-change-log/object-change-log-store";
import {adjustToTimeZone} from "../../../date/date";
import {EntityGraphResponse} from "../../../model/entity-graph";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Sites", "SiteCounties", "RouteSites", "GeofenceSiteGroupSites", "EntityGraph"]});

export const siteApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchSites: builder.query<Site[], SiteSearchCriteria>({
      query: (criteria) => ({
        url: `/sites/search`,
        method: "POST",
        params: criteria
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Sites", id} as const)),
            {type: "Sites", id: "LIST"},
          ];
        } else {
          return [{type: "Sites", id: "LIST"}];
        }
      }
    }),
    getAllSites: builder.query<Site[], string | undefined>({
      query: (regionId) => ({
        url: `/sites/all`,
        method: "GET",
        params: buildParams(regionId)
      }),
      keepUnusedDataFor: 300,
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Sites", id} as const)),
            {type: "Sites", id: "LIST"},
          ];
        } else {
          return [];
        }
      }
    }),
    getSiteCounties: builder.query<string[], SiteCountiesRequest>({
      query: ({regionId, companyId, state}) => ({
        url: `/sites/counties`,
        method: "GET",
        params: removeUndefined({
          regionId,
          companyId,
          state,
        }),
      }),
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, {regionId, companyId, state}) => [{
        type: "SiteCounties",
        id: `${regionId}:${companyId ?? ""}:${state ?? ""}`,
      }],
    }),
    getAllSitesByRoute: builder.query<Site[], { routeId: string, regionId: string }>({
      query: ({routeId, regionId}) => ({
        url: `/sites/search`,
        method: "POST",
        body: {
          regionIds: [regionId],
          routeIds: [routeId],
          routeEffectiveDttm: adjustToTimeZone().getTime(),
        },
        params: buildParams()
      }),
      keepUnusedDataFor: 300,
      providesTags: (result, _error, {routeId}) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Sites", id} as const)),
            {type: 'RouteSites', id: routeId},
          ];
        } else {
          return []
        }
      }
    }),
    getAllSitesByGeofenceSiteGroup: builder.query<Site[], { geofenceSiteGroupId: string, regionId: string }>({
      query: ({geofenceSiteGroupId, regionId}) => ({
        url: `/sites/search`,
        method: "POST",
        body: {
          regionIds: [regionId],
          geofenceSiteGroupIds: [geofenceSiteGroupId],
        },
        params: buildParams()
      }),
      keepUnusedDataFor: 300,
      providesTags: (result, _error, {geofenceSiteGroupId}) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Sites", id} as const)),
            {type: 'GeofenceSiteGroupSites', id: geofenceSiteGroupId},
          ];
        } else {
          return []
        }
      }
    }),
    getSite: builder.query<Site, string>({
      query: (id) => ({
        url: `/sites/${id}`,
        method: "GET",
        params: buildParams()
      }),
      providesTags: (result, error, id) => [{type: "Sites", id}]
    }),
    getSiteEntityGraph: builder.query<EntityGraphResponse, string>({
      query: (id) => ({
        url: `/sites/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "EntityGraph", id: `SITE:${id}`}]
    }),
    createSite: builder.mutation<Site, Partial<Site>>({
      query: (site) => ({
        url: '/sites',
        method: 'POST',
        body: site,
      }),
      invalidatesTags: [{type: 'Sites', id: 'LIST'}],
    }),
    updateSite: builder.mutation<Site, Partial<Site>>({
      query: (site) => ({
        url: '/sites',
        method: 'PUT',
        body: site,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Sites', id}],
    }),
    patchUpdateSite: builder.mutation<Site, { id: string, patch: SitePatchDTO }>({
      query: ({id, patch}) => ({
        url: `/sites/${id}`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Sites', id}],
    }),
    deleteSite: builder.mutation<void, Partial<Site>>({
      query: (site) => ({
        url: '/sites',
        method: 'DELETE',
        body: site,
      }),
      invalidatesTags: (result, error, {id}) => [{type: 'Sites', id}],
    }),
    updateSiteTransportViaPipeline: builder.mutation<void, {
      siteId: string,
      regionId: string,
      transportViaPipeline: boolean
    }>({
      query: ({siteId, transportViaPipeline}) => ({
        url: `/sites/${siteId}/transport-via-pipeline`,
        method: 'PUT',
        body: {
          transportViaPipeline: transportViaPipeline
        },
      }),
      async onQueryStarted({siteId, regionId, transportViaPipeline, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                const statusIndexes = indexesOf(draft, s => s.siteId === siteId)
                if (statusIndexes) {
                  for (const statusIndex of statusIndexes) {
                    const status = draft[statusIndex]
                    status.siteTransportViaPipeline = transportViaPipeline
                  }
                }
              })
            )
          }
          dispatch(containerApi.util.invalidateTags([
            {type: 'Containers'},
            {type: 'SiteContainers', id: siteId},
          ]))
          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: siteId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {siteId}, _meta) => {
        return [
          {type: 'Sites', id: siteId},
          {type: 'Sites', id: 'LIST'},
          {type: 'RouteSites'},
          {type: 'GeofenceSiteGroupSites'},
        ]
      },
    }),
    updateSiteTransportOilViaPipeline: builder.mutation<void, {
      siteId: string,
      regionId: string,
      transportViaPipeline: boolean
    }>({
      query: ({siteId, transportViaPipeline}) => ({
        url: `/sites/${siteId}/transport-oil-via-pipeline`,
        method: 'PUT',
        body: {
          transportViaPipeline: transportViaPipeline
        },
      }),
      async onQueryStarted({siteId, regionId, transportViaPipeline, ...patch}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled
          for (const category of transportCategories) {
            dispatch(
              containerLevelApi.util.updateQueryData('getAllContainerStatuses', {
                regionId: regionId,
                category: category.code
              }, (draft) => {
                const statusIndexes = indexesOf(draft, s => s.siteId === siteId)
                if (statusIndexes) {
                  for (const statusIndex of statusIndexes) {
                    const status = draft[statusIndex]
                    status.siteTransportOilViaPipeline = transportViaPipeline
                  }
                }
              })
            )
          }
          dispatch(containerApi.util.invalidateTags([
            {type: 'Containers'},
            {type: 'SiteContainers', id: siteId},
          ]))
          dispatch(
            objectChangeLogApi.util.invalidateTags([
              {type: 'ObjectObjectChangeLogs', id: siteId}
            ])
          )
        } catch (error) {
          console.debug(error)
        }
      },
      invalidatesTags: (_result, _error, {siteId}, _meta) => {
        return [
          {type: 'Sites', id: siteId},
          {type: 'Sites', id: 'LIST'},
          {type: 'RouteSites'},
          {type: 'GeofenceSiteGroupSites'},
        ]
      },
    }),
  })
});

const buildParams = (regionId?: string) => {
  const extrasParams = {
    withCompany: true,
    withOperatingAreas: true,
    withAfes: false,
    withAssignees: false,
    withAuthorizer: false,
    withContainers: false,
    withLandowner: false,
    withMeters: false,
    withParentSite: false,
    withRigs: false,
    withRoutes: true,
    withTransportData: false,
    withWaterTypeData: true,
    withWells: false,
  }

  if (regionId) {
    return {
      regionId: regionId,
      ...extrasParams
    }
  } else {
    return extrasParams
  }
}

export const {
  useSearchSitesQuery,
  useGetAllSitesQuery,
  useGetSiteCountiesQuery,
  useGetAllSitesByRouteQuery,
  useGetAllSitesByGeofenceSiteGroupQuery,
  useGetSiteQuery,
  useGetSiteEntityGraphQuery,
  useCreateSiteMutation,
  useUpdateSiteMutation,
  usePatchUpdateSiteMutation,
  useDeleteSiteMutation,
  useUpdateSiteTransportViaPipelineMutation,
  useUpdateSiteTransportOilViaPipelineMutation
} = siteApi;
